import { TurnAssignment, ComboEffect } from '../types';
import { Relic } from '../relics/Relic';
import { FEATURES } from '../features';
import { hasTrait } from '../cards/CardTraits';

export function detectCombos(assignment: TurnAssignment, relics: Relic[] = []): ComboEffect[] {
  const combos: ComboEffect[] = [];
  const allCards = [...assignment.attack, ...assignment.defense, ...assignment.ability]
    .filter(card => card.kind !== 'curse');
  const attackNumbers = assignment.attack.filter(card => card.kind !== 'curse');
  const defenseNumbers = assignment.defense.filter(card => card.kind !== 'curse');
  const abilityNumbers = assignment.ability.filter(card => card.kind !== 'curse');
  // Null Stone: treat negatives as positive for combo detection
  const nullStone = relics.some(r => r.id === 'null_stone');
  const playedNumbers = allCards.map(c => nullStone ? Math.abs(c.number) : c.number);
  const zonesUsed = [
    assignment.attack.some(card => card.kind !== 'curse'),
    assignment.defense.some(card => card.kind !== 'curse'),
    assignment.ability.some(card => card.kind !== 'curse'),
  ].filter(Boolean).length;

  // Perfect 10: any Attack + Defense card pair sums to exactly 10
  if (attackNumbers.length > 0 && defenseNumbers.length > 0) {
    let perfectTenFound = false;
    for (const attackCard of attackNumbers) {
      const atkNum = nullStone ? Math.abs(attackCard.number) : attackCard.number;
      for (const defenseCard of defenseNumbers) {
        const defNum = nullStone ? Math.abs(defenseCard.number) : defenseCard.number;
        if (atkNum + defNum === 10) {
          perfectTenFound = true;
          break;
        }
      }
      if (perfectTenFound) break;
    }

    if (perfectTenFound) {
      combos.push({
        name: 'PERFECT 10',
        description: 'ATK + DEF = 10! +3 damage, +3 block',
        bonusDamage: 3,
        bonusBlock: 3,
        bonusHeal: 0,
        bonusDraw: 0,
      });
    }
  }

  // All In: All 3 zones filled
  if (zonesUsed === 3) {
    combos.push({
      name: 'ALL IN',
      description: 'All zones filled! +2 damage, +2 block',
      bonusDamage: 2,
      bonusBlock: 2,
      bonusHeal: 0,
      bonusDraw: 0,
    });
  }

  // Barrage: 3+ cards in attack zone
  if (attackNumbers.length >= 3) {
    combos.push({
      name: 'BARRAGE',
      description: '3+ ATK cards! +6 damage',
      bonusDamage: 6,
      bonusBlock: 0,
      bonusHeal: 0,
      bonusDraw: 0,
    });
  }

  // Bastion: 3+ cards in defense zone
  if (defenseNumbers.length >= 3) {
    combos.push({
      name: 'BASTION',
      description: '3+ DEF cards! +8 block',
      bonusDamage: 0,
      bonusBlock: 8,
      bonusHeal: 0,
      bonusDraw: 0,
    });
  }

  // Arcane Net: at least 2 ability cards with at least one active ability modifier
  const abilityCardsWithActiveMods = abilityNumbers.filter(card =>
    card.modifiers.some(mod => mod.trigger === 'ability'),
  );
  if (abilityCardsWithActiveMods.length >= 2) {
    combos.push({
      name: 'ARCANE NET',
      description: '2+ active ABL cards! heal 2, draw +1',
      bonusDamage: 0,
      bonusBlock: 0,
      bonusHeal: 2,
      bonusDraw: 1,
    });
  }

  // Low Ball: All played cards <= 3
  if (playedNumbers.length > 0 && playedNumbers.every(n => Math.abs(n) <= 3)) {
    combos.push({
      name: 'LOW BALL',
      description: 'All small cards! +5 damage',
      bonusDamage: 5,
      bonusBlock: 0,
      bonusHeal: 0,
      bonusDraw: 0,
    });
  }

  // High Roller: All played cards >= 8
  if (playedNumbers.length > 0 && playedNumbers.every(n => Math.abs(n) >= 8)) {
    combos.push({
      name: 'HIGH ROLLER',
      description: 'All big cards! +8 block',
      bonusDamage: 0,
      bonusBlock: 8,
      bonusHeal: 0,
      bonusDraw: 0,
    });
  }

  // The Pair: Same number in 2 different zones
  if (zonesUsed >= 2) {
    const byZone = {
      attack: assignment.attack.filter(card => card.kind !== 'curse').map(card => nullStone ? Math.abs(card.number) : card.number),
      defense: assignment.defense.filter(card => card.kind !== 'curse').map(card => nullStone ? Math.abs(card.number) : card.number),
      ability: assignment.ability.filter(card => card.kind !== 'curse').map(card => nullStone ? Math.abs(card.number) : card.number),
    };

    const hasPair = (
      byZone.attack.some(n => byZone.defense.includes(n) || byZone.ability.includes(n)) ||
      byZone.defense.some(n => byZone.ability.includes(n))
    );

    if (hasPair) {
      combos.push({
        name: 'THE PAIR',
        description: 'Same number twice! +4 damage',
        bonusDamage: 4,
        bonusBlock: 0,
        bonusHeal: 0,
        bonusDraw: 0,
      });
    }
  }

  // Opposites: One positive and one negative card played
  const hasPositive = playedNumbers.some(n => n > 0);
  const hasNegative = playedNumbers.some(n => n < 0);
  if (hasPositive && hasNegative) {
    combos.push({
      name: 'OPPOSITES',
      description: 'Positive meets negative! Heal 3',
      bonusDamage: 0,
      bonusBlock: 0,
      bonusHeal: 3,
      bonusDraw: 0,
    });
  }

  // Sequential Run: cards are consecutive numbers (e.g., 3,4,5)
  if (playedNumbers.length >= 2) {
    const sorted = [...playedNumbers].sort((a, b) => a - b);
    let isSequential = true;
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] !== sorted[i - 1] + 1) {
        isSequential = false;
        break;
      }
    }
    if (isSequential) {
      combos.push({
        name: 'STRAIGHT',
        description: 'Sequential cards! Draw +1 next turn',
        bonusDamage: 0,
        bonusBlock: 0,
        bonusHeal: 0,
        bonusDraw: 1,
      });
    }
  }

  if (FEATURES.cardTraits) {
    const numberCards = allCards.filter(card => card.kind === 'number');
    if (numberCards.length > 0) {
      const hasOdd = numberCards.some(card => hasTrait(card, 'odd'));
      const hasEven = numberCards.some(card => hasTrait(card, 'even'));
      if (hasOdd && hasEven) {
        combos.push({
          name: 'PARITY SHIFT',
          description: 'Odd + Even mix! +4 damage, +2 block',
          bonusDamage: 4,
          bonusBlock: 2,
          bonusHeal: 0,
          bonusDraw: 0,
        });
      }

      const primeCount = numberCards.filter(card => hasTrait(card, 'prime')).length;
      if (primeCount >= 2) {
        combos.push({
          name: 'PRIME SET',
          description: 'Two prime cards! Draw +1',
          bonusDamage: 0,
          bonusBlock: 0,
          bonusHeal: 0,
          bonusDraw: 1,
        });
      }

      const hasNegative = numberCards.some(card => hasTrait(card, 'negative'));
      const hasHighPositive = numberCards.some(card => hasTrait(card, 'high') && card.number > 0);
      if (hasNegative && hasHighPositive) {
        combos.push({
          name: 'POLAR SPIKE',
          description: 'High positive + negative! Heal 3, brittle enemy',
          bonusDamage: 0,
          bonusBlock: 0,
          bonusHeal: 3,
          bonusDraw: 0,
        });
      }

      const lowCount = numberCards.filter(card => hasTrait(card, 'low')).length;
      const hasHigh = numberCards.some(card => hasTrait(card, 'high'));
      if (lowCount >= 2 && hasHigh) {
        combos.push({
          name: 'ANCHOR SWING',
          description: 'Low swarm + high anchor! +2 damage, +5 block',
          bonusDamage: 2,
          bonusBlock: 5,
          bonusHeal: 0,
          bonusDraw: 0,
        });
      }

      if (zonesUsed === 3 && primeCount >= 1) {
        combos.push({
          name: 'TRINITY PRIME',
          description: 'All zones + prime card! +3 damage, draw +1',
          bonusDamage: 3,
          bonusBlock: 0,
          bonusHeal: 0,
          bonusDraw: 1,
        });
      }

      const negativeCount = numberCards.filter(card => hasTrait(card, 'negative')).length;
      if (negativeCount >= 2) {
        combos.push({
          name: 'DARK CURRENT',
          description: 'Two negatives played! +6 damage',
          bonusDamage: 6,
          bonusBlock: 0,
          bonusHeal: 0,
          bonusDraw: 0,
        });
      }
    }
  }

  return combos;
}
